import { 
  Box, 
  Button, 
  FormControl, 
  FormLabel, 
  Input, 
  Textarea, 
  VStack, 
  HStack,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Checkbox,
  CheckboxGroup
} from '@chakra-ui/react';
import { useState } from 'react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';

// GraphQL mutations and queries for email blast and distribution lists
const SEND_BLAST_EMAIL = gql`
  mutation SendBlastEmail($input: SendBlastEmailInput!) {
    sendBlastEmail(input: $input) {
      success
      message
      executionArn
    }
  }
`;

const LIST_DISTRIBUTION_LISTS = gql`
  query ListDistributionLists {
    listDistributionLists {
      distribution_lists {
        list_id
        list_name
        description
        list_type
        member_count
        member_emails
        is_default
      }
    }
  }
`;

const CREATE_DISTRIBUTION_LIST = gql`
  mutation CreateDistributionList($input: DistributionListInput!) {
    createDistributionList(input: $input) {
      list_id
      list_name
      description
      list_type
      member_count
      member_emails
    }
  }
`;

const ADD_EMAILS_TO_LIST = gql`
  mutation AddEmailsToDistributionList($list_id: String!, $emails: [String!]!) {
    addEmailsToDistributionList(list_id: $list_id, emails: $emails) {
      list_id
      list_name
      member_count
      member_emails
    }
  }
`;

const REMOVE_EMAILS_FROM_LIST = gql`
  mutation RemoveEmailsFromDistributionList($list_id: String!, $emails: [String!]!) {
    removeEmailsFromDistributionList(list_id: $list_id, emails: $emails) {
      list_id
      list_name
      member_count
      member_emails
    }
  }
`;

const DELETE_DISTRIBUTION_LIST = gql`
  mutation DeleteDistributionList($list_id: String!) {
    deleteDistributionList(list_id: $list_id) {
      success
      message
    }
  }
`;

const GET_DISTRIBUTION_LIST_DETAILS = gql`
  query GetDistributionListDetails($list_id: String!) {
    getDistributionListDetails(list_id: $list_id) {
      list_id
      list_name
      description
      list_type
      member_count
      member_emails
    }
  }
`;

const UPDATE_DISTRIBUTION_LIST = gql`
  mutation UpdateDistributionList($list_id: String!, $input: UpdateDistributionListInput!) {
    updateDistributionList(list_id: $list_id, input: $input) {
      list_id
      list_name
      description
      list_type
      member_count
      member_emails
    }
  }
`;

export const MemberEmails = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [contentType, setContentType] = useState('text');
  const [selectedDistributionList, setSelectedDistributionList] = useState('all-members');
  const [isLoading, setIsLoading] = useState(false);
  
  // Distribution list management state
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListType, setNewListType] = useState('custom');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [isCreatingList, setIsCreatingList] = useState(false);
  const [isUpdatingList, setIsUpdatingList] = useState(false);
  
  const toast = useToast();
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isPreviewModalOpen, onOpen: onPreviewModalOpen, onClose: onPreviewModalClose } = useDisclosure();
  const [editingList, setEditingList] = useState(null);
  
  // Apollo hooks
  const [sendBlastEmailMutation] = useMutation(SEND_BLAST_EMAIL);
  const [createDistributionListMutation] = useMutation(CREATE_DISTRIBUTION_LIST);
  const [addEmailsToListMutation] = useMutation(ADD_EMAILS_TO_LIST);
  const [removeEmailsFromListMutation] = useMutation(REMOVE_EMAILS_FROM_LIST);
  const [deleteDistributionListMutation] = useMutation(DELETE_DISTRIBUTION_LIST);
  const [updateDistributionListMutation] = useMutation(UPDATE_DISTRIBUTION_LIST);
  
  const { data: distributionListsData, loading: listsLoading, refetch: refetchLists } = useQuery(LIST_DISTRIBUTION_LISTS);

  const handleSendEmail = async () => {
    // Validation
    if (!subject.trim()) {
      toast({
        title: 'Subject Required',
        description: 'Please enter a subject line for the email.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!body.trim()) {
      toast({
        title: 'Message Body Required',
        description: 'Please enter a message body for the email.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Call the GraphQL mutation to trigger the Step Function
      const result = await sendBlastEmailMutation({
        variables: {
          input: {
            subject: subject.trim(),
            body: body.trim(),
            contentType: contentType,
            distributionListId: selectedDistributionList,
            timestamp: new Date().toISOString(),
          }
        }
      });

      if (result.data?.sendBlastEmail?.success) {
        // Find the selected list name for the success message
        const selectedList = distributionListsData?.listDistributionLists?.distribution_lists?.find(
          list => list.list_id === selectedDistributionList
        );
        const listName = selectedList?.list_name || selectedDistributionList;
        
        toast({
          title: 'Email Sent Successfully',
          description: `Blast email "${subject}" has been sent to "${listName}".`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Clear form after successful send
        setSubject('');
        setBody('');
        setContentType('text');
      } else {
        throw new Error(result.data?.sendBlastEmail?.message || 'Unknown error');
      }
      
    } catch (error) {
      console.error('Email send error:', error);
      
      toast({
        title: 'Email Send Failed',
        description: error.message || 'There was an error sending the email. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = () => {
    onPreviewModalOpen();
  };

  // Distribution list management functions
  const handleCreateList = async () => {
    if (!newListName.trim()) {
      toast({
        title: 'List Name Required',
        description: 'Please enter a name for the distribution list.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreatingList(true);
    try {
      const result = await createDistributionListMutation({
        variables: {
          input: {
            list_name: newListName.trim(),
            description: newListDescription.trim(),
            list_type: newListType,
            member_emails: selectedEmails
          }
        }
      });

      if (result.data?.createDistributionList) {
        toast({
          title: 'Distribution List Created',
          description: `"${newListName}" has been created successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Reset form and close modal
        setNewListName('');
        setNewListDescription('');
        setNewListType('custom');
        setSelectedEmails([]);
        onCreateModalClose();
        refetchLists();
      }
    } catch (error) {
      console.error('Create list error:', error);
      toast({
        title: 'Failed to Create List',
        description: error.message || 'There was an error creating the distribution list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingList(false);
    }
  };

  const handleDeleteList = async (listId, listName) => {
    if (!window.confirm(`Are you sure you want to delete the distribution list "${listName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await deleteDistributionListMutation({
        variables: { list_id: listId }
      });

      if (result.data?.deleteDistributionList?.success) {
        toast({
          title: 'Distribution List Deleted',
          description: `"${listName}" has been deleted successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        refetchLists();
      }
    } catch (error) {
      console.error('Delete list error:', error);
      toast({
        title: 'Failed to Delete List',
        description: error.message || 'There was an error deleting the distribution list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openCreateModal = () => {
    setNewListName('');
    setNewListDescription('');
    setNewListType('custom');
    setSelectedEmails([]);
    onCreateModalOpen();
  };

  const openEditModal = (list) => {
    setEditingList(list);
    setNewListName(list.list_name);
    setNewListDescription(list.description || '');
    setNewListType(list.list_type);
    setSelectedEmails(list.member_emails || []);
    onEditModalOpen();
  };

  const handleUpdateList = async () => {
    if (!newListName.trim()) {
      toast({
        title: 'List Name Required',
        description: 'Please enter a name for the distribution list.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUpdatingList(true);
    try {
      const result = await updateDistributionListMutation({
        variables: {
          list_id: editingList.list_id,
          input: {
            list_name: newListName.trim(),
            description: newListDescription.trim(),
            member_emails: selectedEmails
          }
        }
      });

      if (result.data?.updateDistributionList) {
        toast({
          title: 'Distribution List Updated',
          description: `"${newListName}" has been updated successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Reset form and close modal
        setNewListName('');
        setNewListDescription('');
        setNewListType('custom');
        setSelectedEmails([]);
        setEditingList(null);
        onEditModalClose();
        refetchLists();
      }
    } catch (error) {
      console.error('Update list error:', error);
      toast({
        title: 'Failed to Update List',
        description: error.message || 'There was an error updating the distribution list.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingList(false);
    }
  };

  return (
    <Box maxW="1200px" mx="auto">
      <Card>
        <CardHeader>
          <Heading size="lg">Member Email Management</Heading>
          <Text color="gray.600" mt={2}>
            Manage distribution lists and send email blasts to club members.
          </Text>
        </CardHeader>
        
        <CardBody>
          <Tabs>
            <TabList>
              <Tab>Distribution Lists</Tab>
              <Tab>Send Email</Tab>
            </TabList>

            <TabPanels>
              {/* Distribution Lists Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <Heading size="md">Manage Distribution Lists</Heading>
                    <Button colorScheme="blue" onClick={openCreateModal}>
                      Create New List
                    </Button>
                  </HStack>

                  {listsLoading ? (
                    <Text>Loading distribution lists...</Text>
                  ) : (
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>List Name</Th>
                          <Th>Description</Th>
                          <Th>Type</Th>
                          <Th>Members</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {distributionListsData?.listDistributionLists?.distribution_lists?.map((list) => (
                          <Tr key={list.list_id}>
                            <Td fontWeight="semibold">{list.list_name}</Td>
                            <Td>{list.description || 'No description'}</Td>
                            <Td>
                              <Badge colorScheme={list.list_type === 'default' ? 'green' : 'blue'}>
                                {list.list_type}
                              </Badge>
                            </Td>
                            <Td>{list.member_count || 0}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <IconButton
                                  aria-label="Edit list"
                                  icon={<EditIcon />}
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditModal(list)}
                                />
                                {list.list_type !== 'default' && (
                                  <IconButton
                                    aria-label="Delete list"
                                    icon={<DeleteIcon />}
                                    size="sm"
                                    colorScheme="red"
                                    variant="outline"
                                    onClick={() => handleDeleteList(list.list_id, list.list_name)}
                                  />
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  )}
                </VStack>
              </TabPanel>

              {/* Send Email Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {/* Warning Alert */}
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Important Notice</AlertTitle>
                      <AlertDescription>
                        This will send an email to the selected distribution list. Please review your message carefully before sending.
                      </AlertDescription>
                    </Box>
                  </Alert>

                  {/* Subject Line */}
                  <FormControl isRequired>
                    <FormLabel>Subject Line</FormLabel>
                    <Input
                      placeholder="Enter email subject..."
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      maxLength={200}
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {subject.length}/200 characters
                    </Text>
                  </FormControl>

                  {/* Distribution List Selection */}
                  <FormControl>
                    <FormLabel>Distribution List</FormLabel>
                    <Select 
                      value={selectedDistributionList} 
                      onChange={(e) => setSelectedDistributionList(e.target.value)}
                      isDisabled={listsLoading}
                    >
                      {listsLoading ? (
                        <option>Loading distribution lists...</option>
                      ) : (
                        distributionListsData?.listDistributionLists?.distribution_lists?.map((list) => {
                          // For "All Members" list, show "dynamic" instead of member count
                          const displayText = list.list_id === 'all-members' 
                            ? `${list.list_name} (dynamic - all active members)`
                            : `${list.list_name} (${list.member_count || 0} members)`;
                          
                          return (
                            <option key={list.list_id} value={list.list_id}>
                              {displayText}
                            </option>
                          );
                        })
                      )}
                    </Select>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      Select which group of members to send this email to
                    </Text>
                  </FormControl>

                  {/* Content Type Selection */}
                  <FormControl>
                    <FormLabel>Message Format</FormLabel>
                    <RadioGroup value={contentType} onChange={setContentType}>
                      <Stack direction="row" spacing={6}>
                        <Radio value="text">Plain Text</Radio>
                        <Radio value="html">HTML</Radio>
                      </Stack>
                    </RadioGroup>
                  </FormControl>

                  {/* Message Body */}
                  <FormControl isRequired>
                    <FormLabel>
                      Message Body 
                      {contentType === 'html' && (
                        <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                          (HTML tags supported)
                        </Text>
                      )}
                    </FormLabel>
                    <Textarea
                      placeholder={
                        contentType === 'html' 
                          ? "Enter your HTML message...\n\nExample:\n<h2>Important Club Update</h2>\n<p>Dear Members,</p>\n<p>We wanted to inform you about...</p>"
                          : "Enter your message...\n\nDear Members,\n\nWe wanted to inform you about..."
                      }
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      minHeight="300px"
                      fontFamily={contentType === 'html' ? 'monospace' : 'inherit'}
                    />
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      {body.length} characters
                    </Text>
                  </FormControl>

                  <Divider />

                  {/* Action Buttons */}
                  <HStack spacing={4} justify="flex-end">
                    <Button
                      variant="outline"
                      onClick={handlePreview}
                      isDisabled={!subject.trim() || !body.trim()}
                    >
                      Preview Email
                    </Button>
                    <Button
                      colorScheme="blue"
                      onClick={handleSendEmail}
                      isLoading={isLoading}
                      loadingText="Sending..."
                      isDisabled={!subject.trim() || !body.trim()}
                    >
                      Send Email
                    </Button>
                  </HStack>

                  {/* Usage Guidelines */}
                  <Box bg="gray.50" p={4} borderRadius="md" mt={6}>
                    <Text fontWeight="semibold" mb={2}>Email Guidelines:</Text>
                    <VStack align="start" spacing={1} fontSize="sm" color="gray.700">
                      <Text>• Keep subject lines clear and descriptive</Text>
                      <Text>• Use professional language appropriate for all members</Text>
                      <Text>• Include relevant club information and contact details</Text>
                      <Text>• For HTML emails, test formatting before sending</Text>
                      <Text>• Avoid excessive use of blast emails to prevent member fatigue</Text>
                    </VStack>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      {/* Create Distribution List Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={onCreateModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Distribution List</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>List Name</FormLabel>
                <Input
                  placeholder="Enter list name..."
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Enter list description..."
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>List Type</FormLabel>
                <Select value={newListType} onChange={(e) => setNewListType(e.target.value)}>
                  <option value="custom">Custom</option>
                  <option value="committee">Committee</option>
                  <option value="event">Event</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Initial Members (Optional)</FormLabel>
                <Textarea
                  placeholder="Enter email addresses, one per line..."
                  value={selectedEmails.join('\n')}
                  onChange={(e) => setSelectedEmails(e.target.value.split('\n').filter(email => email.trim()))}
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  You can add members later after creating the list
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCreateList}
              isLoading={isCreatingList}
              loadingText="Creating..."
            >
              Create List
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Distribution List Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Distribution List</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>List Name</FormLabel>
                <Input
                  placeholder="Enter list name..."
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  placeholder="Enter list description..."
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>List Type</FormLabel>
                <Select value={newListType} isDisabled>
                  <option value="custom">Custom</option>
                  <option value="committee">Committee</option>
                  <option value="event">Event</option>
                </Select>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  List type cannot be changed after creation
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel>Member Emails</FormLabel>
                <Textarea
                  placeholder="Enter email addresses, one per line..."
                  value={selectedEmails.join('\n')}
                  onChange={(e) => setSelectedEmails(e.target.value.split('\n').filter(email => email.trim()))}
                  minHeight="150px"
                />
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Current member count: {selectedEmails.length}
                </Text>
              </FormControl>

              {editingList?.list_type === 'default' && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    This is a default system list. Some changes may not be allowed.
                  </AlertDescription>
                </Alert>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUpdateList}
              isLoading={isUpdatingList}
              loadingText="Updating..."
            >
              Update List
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Preview Email Modal */}
      <Modal isOpen={isPreviewModalOpen} onClose={onPreviewModalClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>Email Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={1}>
                  Subject:
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {subject || '(No subject)'}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={1}>
                  Distribution List:
                </Text>
                <Text>
                  {distributionListsData?.listDistributionLists?.distribution_lists?.find(
                    list => list.list_id === selectedDistributionList
                  )?.list_name || selectedDistributionList}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={1}>
                  Format:
                </Text>
                <Badge colorScheme={contentType === 'html' ? 'blue' : 'gray'}>
                  {contentType === 'html' ? 'HTML' : 'Plain Text'}
                </Badge>
              </Box>

              <Divider />

              <Box>
                <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
                  Message Body:
                </Text>
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                  bg="white"
                  maxH="400px"
                  overflowY="auto"
                >
                  {contentType === 'html' ? (
                    <Box dangerouslySetInnerHTML={{ __html: body || '<em>(No content)</em>' }} />
                  ) : (
                    <Text whiteSpace="pre-wrap" fontFamily="inherit">
                      {body || '(No content)'}
                    </Text>
                  )}
                </Box>
              </Box>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This is a preview of how your email will appear. The actual email will be sent from no-reply@goldengunclub.us.
                </AlertDescription>
              </Alert>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPreviewModalClose}>
              Close
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onPreviewModalClose();
                handleSendEmail();
              }}
              isDisabled={!subject.trim() || !body.trim()}
            >
              Send Email
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};